import {
  FieldError,
  Input as AriaInput,
  Label as AriaLabel,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
  composeRenderProps,
  type InputRenderProps,
} from "react-aria-components";
import { cva, type VariantProps } from "class-variance-authority";
import { type ReactNode } from "react";
import WarningSolid from "src/design-system/icons/WarningSolid.svg?react";
import { useIntl } from "react-intl";
import XCircleSolid from "src/design-system/icons/XCircleSolid.svg?react";
import styles from "./Field.module.css";
import { fieldMessages } from "./Field.messages";

const inputVariants = cva(styles["input"], {
  variants: {
    fieldSize: {
      default: styles["default"],
      large: styles["large"],
    },
    filled: {
      true: styles["filled"],
      false: "",
    },
    withIconLeft: {
      true: styles["with-icon-left"],
      false: "",
    },
    withClearBtn: {
      true: styles["with-clear-btn"],
      false: "",
    },
    // For Storybook forced states
    state: {
      default: "",
      hover: styles["hover"],
      focus: styles["focus"],
      active: styles["active"],
      disabled: styles["disabled"],
      error: styles["error"],
    },
  },
  defaultVariants: {
    fieldSize: "default",
    filled: false,
    withIconLeft: false,
    withClearBtn: false,
    state: "default",
  },
});

export type FieldProps = AriaTextFieldProps &
  VariantProps<typeof inputVariants> & {
    label?: string;
    description?: string;
    errorMessage?: string | ((validation: ValidationResult) => string);
    placeholder?: string;
    iconLeft?: ReactNode;
    isClearable?: boolean;
    onClear?: () => void;
    fieldSize?: "default" | "large";
    state?: "default" | "hover" | "focus" | "active" | "disabled" | "error";
    inputClassName?: string | ((props: InputRenderProps) => string);
  };

export function Field({
  ref,
  className,
  fieldSize,
  state,
  filled,
  label,
  description,
  errorMessage,
  iconLeft,
  isClearable,
  onClear,
  isDisabled,
  ...props
}: FieldProps & {
  ref?: React.RefObject<HTMLInputElement>;
}) {
  const intl = useIntl();
  const isInvalid = !!(state === "error" || !!errorMessage || props.isInvalid);
  const showClearButton = isClearable && props.value && !isDisabled;

  return (
    <AriaTextField
      className={composeRenderProps(
        className,
        (className) => `${styles["field"]} ${className ?? ""}`,
      )}
      isDisabled={isDisabled || state === "disabled"}
      isInvalid={isInvalid}
      {...props}
    >
      {label && <AriaLabel className={styles["label"]}>{label}</AriaLabel>}
      <div className={styles["field-wrapper"]}>
        {iconLeft && <div className={styles["icon-container"]}>{iconLeft}</div>}
        <AriaInput
          ref={ref}
          className={composeRenderProps(props.inputClassName, (className) =>
            inputVariants({
              fieldSize,
              filled,
              withIconLeft: !!iconLeft,
              withClearBtn: !!showClearButton,
              state,
              className,
            }),
          )}
        />
        {showClearButton && (
          <button
            type="button"
            onClick={onClear}
            className={styles["clear-button"]}
            aria-label={intl.formatMessage(fieldMessages.clearInput)}
            disabled={isDisabled}
          >
            <XCircleSolid className="h-5 w-5 text-icon-primary" />
          </button>
        )}
      </div>
      {description && (
        <Text slot="description" className={styles["description"]}>
          {description}
        </Text>
      )}
      {isInvalid && (
        <FieldError className={styles["error-message"]}>
          {(error) => (
            <>
              <WarningSolid />
              <span className="text-body-sm">
                {typeof errorMessage === "function"
                  ? errorMessage(error)
                  : errorMessage}
              </span>
            </>
          )}
        </FieldError>
      )}
    </AriaTextField>
  );
}
